import React from 'react';
import { X, Bookmark, Trash2, ExternalLink, ShieldCheck, HelpCircle, Upload } from 'lucide-react';
import type { UnitsMode } from '../types';
import type { ElevationStats } from '../features/gpx/elevation';

type SettingsModalProps = {
  unitsMode: UnitsMode;
  onToggleUnits: () => void;
  themeMode: 'system' | 'light' | 'dark';
  onSetThemeMode: (mode: 'system' | 'light' | 'dark') => void;
  precipEnabled: boolean;
  onTogglePrecip: () => void;
  reminderEnabled: boolean;
  reminderLeadMin: number;
  onToggleReminder: () => void;
  onSetReminderLead: (min: number) => void;
  onImportGpx: (file: File) => void;
  elevationStats: ElevationStats | null;
  onClearRoute: () => void;
  onSaveRoute: () => void;
  canSaveRoute: boolean;
  onClose: () => void;
};

export const SettingsModal: React.FC<SettingsModalProps> = ({
  unitsMode,
  onToggleUnits,
  themeMode,
  onSetThemeMode,
  precipEnabled,
  onTogglePrecip,
  reminderEnabled,
  reminderLeadMin,
  onToggleReminder,
  onSetReminderLead,
  onImportGpx,
  elevationStats,
  onClearRoute,
  onSaveRoute,
  canSaveRoute,
  onClose,
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 990,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="sheet-surface"
        style={{
          width: '100%',
          maxWidth: 480,
          borderTopLeftRadius: 36,
          borderTopRightRadius: 36,
          padding: '16px 20px 32px',
          boxSizing: 'border-box',
          maxHeight: '85vh',
          overflowY: 'auto',
          color: 'var(--label)',
        }}
      >
        {/* Grabber */}
        <div
          style={{
            width: 36,
            height: 5,
            borderRadius: 2.5,
            backgroundColor: 'var(--label3)',
            margin: '0 auto 12px',
          }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="t-title2">Settings & Info</div>
          <button className="cbtn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Preferences Group */}
        <div className="t-cap2 c2" style={{ textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>
          Preferences
        </div>
        <div className="cell-group" style={{ marginBottom: 20 }}>
          {/* Units */}
          <div className="cell" style={{ justifyContent: 'space-between' }}>
            <span className="t-body">Units</span>
            <div className="seg-control" style={{ width: 160 }}>
              <button
                className={`seg-item ${unitsMode === 'imperial' ? 'active' : ''}`}
                onClick={() => {
                  if (unitsMode !== 'imperial') onToggleUnits();
                }}
              >
                °F · mi
              </button>
              <button
                className={`seg-item ${unitsMode === 'metric' ? 'active' : ''}`}
                onClick={() => {
                  if (unitsMode !== 'metric') onToggleUnits();
                }}
              >
                °C · km
              </button>
            </div>
          </div>

          {/* Theme */}
          <div className="cell" style={{ justifyContent: 'space-between' }}>
            <span className="t-body">Appearance</span>
            <div className="seg-control" style={{ width: 190 }}>
              <button
                className={`seg-item ${themeMode === 'system' ? 'active' : ''}`}
                onClick={() => onSetThemeMode('system')}
              >
                Auto
              </button>
              <button
                className={`seg-item ${themeMode === 'light' ? 'active' : ''}`}
                onClick={() => onSetThemeMode('light')}
              >
                Light
              </button>
              <button
                className={`seg-item ${themeMode === 'dark' ? 'active' : ''}`}
                onClick={() => onSetThemeMode('dark')}
              >
                Dark
              </button>
            </div>
          </div>

          <div className="cell" style={{ justifyContent: 'space-between' }}>
            <span className="t-body" id="precip-toggle-label">Hourly precip overlay</span>
            <div
              className={`toggle-switch ${precipEnabled ? 'on' : ''}`}
              onClick={onTogglePrecip}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onTogglePrecip();
                }
              }}
              role="switch"
              tabIndex={0}
              aria-checked={precipEnabled}
              aria-label="Hourly precip overlay"
              aria-labelledby="precip-toggle-label"
            >
              <div className="knob" />
            </div>
          </div>

          <div className="cell" style={{ justifyContent: 'space-between' }}>
            <span className="t-body" id="reminder-toggle-label">Leave reminder</span>
            <div
              className={`toggle-switch ${reminderEnabled ? 'on' : ''}`}
              onClick={onToggleReminder}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onToggleReminder();
                }
              }}
              role="switch"
              tabIndex={0}
              aria-checked={reminderEnabled}
              aria-label="Leave reminder"
              aria-labelledby="reminder-toggle-label"
            >
              <div className="knob" />
            </div>
          </div>
          {reminderEnabled && (
            <div className="cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <span className="t-foot c2">Minutes before departure (fires only while this tab is open)</span>
              <div className="seg-control" style={{ width: '100%' }}>
                {[15, 30, 60, 90].map((min) => (
                  <button
                    key={min}
                    className={`seg-item ${reminderLeadMin === min ? 'active' : ''}`}
                    onClick={() => onSetReminderLead(min)}
                  >
                    {min}m
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Route Actions */}
        <div className="t-cap2 c2" style={{ textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>
          Route Actions
        </div>
        <div className="cell-group" style={{ marginBottom: 20 }}>
          {canSaveRoute && (
            <div
              className="cell"
              style={{ cursor: 'pointer', color: 'var(--accent)' }}
              onClick={() => {
                onSaveRoute();
                onClose();
              }}
            >
              <Bookmark size={18} style={{ marginRight: 12 }} />
              <span className="t-body" style={{ fontWeight: 600 }}>Save origin and destination</span>
            </div>
          )}

          <label
            className="cell"
            style={{ cursor: 'pointer', color: 'var(--accent)' }}
          >
            <Upload size={18} style={{ marginRight: 12 }} />
            <span className="t-body" style={{ fontWeight: 600 }}>Import GPX</span>
            <input
              type="file"
              accept=".gpx,application/gpx+xml,text/xml"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onImportGpx(file);
                e.target.value = '';
              }}
            />
          </label>
          {elevationStats && (
            <div className="cell" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span className="t-foot c2">Elevation along imported track</span>
              <span className="t-body">
                ↑ {Math.round(elevationStats.totalAscent)} m · ↓ {Math.round(elevationStats.totalDescent)} m · max {Math.round(elevationStats.maxElevation)} m
              </span>
            </div>
          )}

          <div
            className="cell"
            style={{ cursor: 'pointer', color: 'var(--danger)' }}
            onClick={() => {
              onClearRoute();
              onClose();
            }}
          >
            <Trash2 size={18} style={{ marginRight: 12 }} />
            <span className="t-body" style={{ fontWeight: 600 }}>Clear Route</span>
          </div>
        </div>

        {/* Support & Privacy */}
        <div className="t-cap2 c2" style={{ textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>
          Support & Privacy
        </div>
        <div className="cell-group" style={{ marginBottom: 20 }}>
          <a
            href="https://owlsoftware.studio/"
            target="_blank"
            rel="noopener noreferrer"
            className="cell"
            style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <HelpCircle size={18} color="var(--accent)" />
              <span className="t-body">Customer Support</span>
            </div>
            <ExternalLink size={16} className="c2" />
          </a>

          <div className="cell" style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '12px 16px', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <ShieldCheck size={18} color="#34C759" />
              <span className="t-headline">Privacy notice</span>
            </div>
            <p className="t-foot c2" style={{ margin: 0, lineHeight: 1.45 }}>
              OnTheRoute respects your privacy. No personal data, location history, or identifiers leave your device, except for outgoing queries to the public mapping and weather APIs (OpenFreeMap, OSRM, Open-Meteo, NWS, WeatherAPI precip tiles, Photon) required to compute your route and forecast. Saved routes and settings are stored locally on your device.
            </p>
          </div>
        </div>

        {/* Legal & Attribution */}
        <div className="t-cap2 c2" style={{ textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>
          Attribution & Data
        </div>
        <div className="card-surface" style={{ padding: 14, marginBottom: 20 }}>
          <div className="t-foot c2" style={{ lineHeight: 1.5 }}>
            • <b>Map & Routing:</b> © OpenStreetMap contributors, OpenFreeMap vector tiles, OSRM public demo.<br />
            • <b>Weather:</b> Open-Meteo free API (hourly unixtime model).<br />
            • <b>Alerts:</b> National Oceanic and Atmospheric Administration (NOAA) / NWS active alerts.<br />
            • <b>Precip overlay:</b> WeatherAPI hourly precipitation forecast tiles (not live radar).<br />
            • <b>Search:</b> Photon by Komoot.
          </div>
        </div>

        <div style={{ textAlign: 'center' }} className="t-cap c3">
          Copyright © 2026 Owl Software Studio · OnTheRoute Web v1.0.0
        </div>
      </div>
    </div>
  );
};
